-- | Step 3 durable editor state and app-runtime adapters over fill-queue
-- | load/save helpers.
-- |
-- | The editor state still wraps the fill-queue record during this milestone,
-- | but the old `Steps.Step3` widget façade has been removed.
module Steps.Step3.State
    ( EditorState
    , FormFillState
    , FormFillRenderOptions
    , PostSaveAction(..)
    , Step3State
    , defaultRenderOptions
    , fromEditorState
    , mapEditorState
    , setEditorState
    , preloadStep3Resources
    , loadInitialStep3State
    , loadTargetStep3State
    , setPendingLoadTarget
    , isBusy
    , saveCurrentDocument
    , shouldSaveCurrentDocument
    , hasEditableFields
    , applySavedDocumentState
    , applyLoadedEditorState
    , applyAutofillResult
    , applyToggleLockResult
    , applyDocumentMutationResult
    , initialLoadOperationKind
    , loadOperationKindForState
    ) where

import Common

import API.Attachments (Attachment)
import API.Autofill (AutofillResult)
import API.Comments (Comment)
import API.Document (Document, FormReport, ToggleLockResult)
import API.FormType (FormType)
import API.Signature (SignatureRefreshBase)
import App.Operation.Types (OperationId, OperationKind)
import App.Route as AppRoute
import Control.Monad.Except (runExceptT)
import Data.Array as Arr
import Data.Maybe (Maybe(..), isJust)
import Error (ErrorWrapper)
import Steps.Step3.FillQueue as FillQueue
import Steps.Step3.Types as Types
import Steps.Step3.Transition (PendingTransition, emptyTransitionState)
import Steps.WorkQueue.Cache (WorkQueueCache, WorkQueuePreloadResult, insertDocument)
import Steps.WorkQueue.Cache as WorkQCache
import Steps.WorkQueue.State (FormSectionID, WorkQueueState)
import Steps.WorkQueue.UIState (WorkQueueUiState)
import Widgets.Form (setFormData)
import Widgets.Comments as Comments
import Widgets.Signature.Group as SigGroup

type EditorState = Types.FillQueueState

type FormFillState = Types.FormFillState

type FormFillRenderOptions = Types.FormFillRenderOptions

data PostSaveAction
    = SaveThenCancel
    | SaveThenFinish
    | SaveThenExport FormReport
    | SaveThenLock

type Step3State =
    { editor :: EditorState
    , saveOperationId :: Maybe OperationId
    , loadOperationId :: Maybe OperationId
    , autofillOperationId :: Maybe OperationId
    , lockOperationId :: Maybe OperationId
    , documentMutationOperationId :: Maybe OperationId
    , pendingPostSave :: Maybe PostSaveAction
    }

defaultRenderOptions :: FormFillRenderOptions
defaultRenderOptions = Types.defaultRenderOptions

fromEditorState :: EditorState -> Step3State
fromEditorState editor =
    { editor
    , saveOperationId: Nothing
    , loadOperationId: Nothing
    , autofillOperationId: Nothing
    , lockOperationId: Nothing
    , documentMutationOperationId: Nothing
    , pendingPostSave: Nothing
    }

mapEditorState :: (EditorState -> EditorState) -> Step3State -> Step3State
mapEditorState fn state = state { editor = fn state.editor }

setEditorState :: EditorState -> Step3State -> Step3State
setEditorState editor state = state { editor = editor }

preloadStep3Resources
    :: WorkQueueState
    -> WorkQueueCache
    -> Maybe FormType
    -> Maybe Document
    -> Aff (Either ErrorWrapper WorkQueuePreloadResult)
preloadStep3Resources workQueueState workQueueCache maybeFormType maybeDoc =
    runExceptT $ WorkQCache.preloadStep3Resources workQueueState workQueueCache maybeFormType maybeDoc

loadInitialStep3State
    :: WorkQueueState
    -> WorkQueueUiState
    -> WorkQueueCache
    -> Maybe Document
    -> Maybe FormSectionID
    -> Aff (Either ErrorWrapper Step3State)
loadInitialStep3State workQueueState workQueueUi workQueueCache doc initialAnchor =
    map fromEditorState <$> FillQueue.loadInitFillQueue workQueueState workQueueUi workQueueCache doc emptyTransitionState initialAnchor

loadTargetStep3State :: Step3State -> Maybe Document -> Aff (Either ErrorWrapper Step3State)
loadTargetStep3State current doc =
    map (applyLoadedEditorState current) <$> FillQueue.loadFillState current.editor doc

setPendingLoadTarget :: PendingTransition -> Step3State -> Step3State
setPendingLoadTarget target state =
    state
        { editor = state.editor
            { position = target.position
            , anchor = target.anchor
            , activeSection = target.anchor
            , sectionIntent = maybe AppRoute.SectionClear AppRoute.SectionSet target.anchor
            }
        }

isBusy :: Step3State -> Boolean
isBusy state =
    isJust state.saveOperationId
        || isJust state.loadOperationId
        || isJust state.autofillOperationId
        || isJust state.lockOperationId
        || isJust state.documentMutationOperationId

saveCurrentDocument :: Step3State -> Aff (Either ErrorWrapper Document)
saveCurrentDocument = FillQueue.saveDocument <<< _.editor

shouldSaveCurrentDocument :: Step3State -> Boolean
shouldSaveCurrentDocument = FillQueue.shouldSaveDocument <<< _.editor

hasEditableFields :: Step3State -> Boolean
hasEditableFields = Types.hasEditableFields <<< _.editor.fillState

applySavedDocumentState :: Step3State -> Document -> Either ErrorWrapper Step3State
applySavedDocumentState state doc =
    map (\editor -> state { editor = editor, saveOperationId = Nothing }) $
        FillQueue.applySavedDocument state.editor doc

applyLoadedEditorState :: Step3State -> EditorState -> Step3State
applyLoadedEditorState waitState loadedState =
    waitState
        { editor = FillQueue.mergeTransitionWaitState waitState.editor loadedState
        , loadOperationId = Nothing
        }

applyAutofillResult :: Step3State -> AutofillResult -> Either ErrorWrapper Step3State
applyAutofillResult state res = do
    let editor = state.editor
    let formCtx = (Types.formContext editor.workQueueCache editor.fillState) { isAutofilling = true }
    let newDoc = editor.fillState.doc { employer = res.employer }
    newFormState <- setFormData formCtx res.data editor.fillState.form
    pure $ state
        { editor = editor
            { fillState = editor.fillState
                { form = newFormState
                , doc = newDoc
                , signatures = SigGroup.updateDefSignatures editor.fillState.signatures res.signature_fields
                }
            }
        , autofillOperationId = Nothing
        }

applyToggleLockResult :: Step3State -> ToggleLockResult -> Step3State
applyToggleLockResult state result =
    applyFillStateUpdate (applySignatureRefresh result)
        state
            { editor = Types.toggleLock result state.editor
            , lockOperationId = Nothing
            }

applyDocumentMutationResult :: Step3State -> Types.DocumentMutationResult -> Either ErrorWrapper Step3State
applyDocumentMutationResult state result =
    pure $ applyFillStateUpdate (applyResult result) state

applyResult :: Types.DocumentMutationResult -> FormFillState -> FormFillState
applyResult = case _ of
    Types.CommentCreated comment ->
        updateComments (appendComment comment) (\_ -> Comments.Posting { text: "", public: comment.public })

    Types.CommentUpdated comment ->
        updateComments (replaceComment comment) (\_ -> Comments.Posting { text: "", public: comment.public })

    Types.CommentDeleted commentId ->
        updateComments (Arr.filter (\comment -> comment.id /= commentId)) keepCommentState

    Types.AttachmentCreated attachment ->
        updateAttachments (_ <> [ attachment ])

    Types.AttachmentDeleted attachmentId ->
        updateAttachments (Arr.filter (\attachment -> attachment.id /= attachmentId))

    Types.SignatureSigned signature ->
        updateSignatures
            (SigGroup.loadSignatures { signatures: [ signature ], verifications: [], signature_fields: [] })
            (\doc -> doc { signatures = upsertBy _.key [ signature ] doc.signatures })

    Types.SignatureSignedAndRefreshed signature refresh ->
        updateSignatures
            (SigGroup.loadSignatures { signatures: [ signature ], verifications: [], signature_fields: [] })
            (\doc -> doc { signatures = upsertBy _.key [ signature ] doc.signatures })
            <<< applySignatureRefresh refresh

    Types.SignatureGroupRefreshed refresh ->
        applySignatureRefresh refresh

    Types.SignaturesRequested verifications ->
        updateSignatures
            (SigGroup.loadVerifications verifications)
            (\doc -> doc { verifications = upsertBy (_.data.key) verifications doc.verifications })
  where
    keepCommentState state = state.post

applyFillStateUpdate :: (FormFillState -> FormFillState) -> Step3State -> Step3State
applyFillStateUpdate fn state =
    state
        { editor = state.editor
            { fillState = fillState
            , workQueueState = insertDocument fillState.doc state.editor.workQueueState
            }
        }
  where
    fillState = fn state.editor.fillState

updateComments
    :: (Array Comment -> Array Comment)
    -> (Comments.Comments -> Comments.CommentState)
    -> FormFillState
    -> FormFillState
updateComments updateList nextPost fillState =
    fillState
        { comments = fillState.comments
            { comments = comments
            , post = nextPost fillState.comments
            }
        , doc = fillState.doc { comments = comments }
        }
  where
    comments = updateList fillState.comments.comments

appendComment :: Comment -> Array Comment -> Array Comment
appendComment comment comments =
    comments <> [ comment ]

replaceComment :: Comment -> Array Comment -> Array Comment
replaceComment comment =
    map \current ->
        if current.id == comment.id then comment else current

updateAttachments :: (Array Attachment -> Array Attachment) -> FormFillState -> FormFillState
updateAttachments updateList fillState =
    fillState
        { attachments = fillState.attachments { attachments = attachments }
        , doc = fillState.doc { attachments = attachments }
        }
  where
    attachments = updateList fillState.attachments.attachments

updateSignatures
    :: (SigGroup.SigGroupState -> SigGroup.SigGroupState)
    -> (Document -> Document)
    -> FormFillState
    -> FormFillState
updateSignatures updateGroup updateDoc fillState =
    fillState
        { signatures = updateGroup fillState.signatures
        , doc = updateDoc fillState.doc
        }

applySignatureRefresh :: forall r. SignatureRefreshBase r -> FormFillState -> FormFillState
applySignatureRefresh refresh fillState =
    fillState
        { signatures = SigGroup.loadSignatures refresh $
            -- @todo: code smell alert!
            -- This closely ties this module to the signature group widget.
            SigGroup.refreshFieldPermissions refresh.signature_fields fillState.signatures
        , form = fillState.form { access = refresh.access }
        , doc = fillState.doc
            { signatures = refresh.signatures
            , verifications = refresh.verifications
            , signature_fields = refresh.signature_fields
            , locked_at = refresh.locked_at
            , access = refresh.access
            , can = fillState.doc.can
                { lock = refresh.can.lock
                , update = refresh.can.update
                }
            }
        }

upsertBy :: forall a k. Eq k => (a -> k) -> Array a -> Array a -> Array a
upsertBy key updates existing =
    updates <> Arr.filter (\current -> not $ Arr.any (\new -> key new == key current) updates) existing

initialLoadOperationKind :: WorkQueueState -> Maybe Document -> OperationKind
initialLoadOperationKind = Types.initialLoadOperationKind

loadOperationKindForState :: Step3State -> OperationKind
loadOperationKindForState = Types.loadOperationKindForState <<< _.editor
